/**
 * Coerce persisted DATE strings back into `Date` objects for the form layer.
 *
 * WHY THIS EXISTS — PrimeNG's `<p-datepicker>` renders a string it cannot
 * parse as an EMPTY input, silently. `writeValue` runs the incoming value
 * through `parseValueFromString` against the configured `dateFormat`
 * (`"yy-mm-dd"` on every filing sheet); an API `"01/01/2023"` or an ISO
 * date-time throws there, and `formatDateTime` then falls through to `''`.
 * The user sees a blank field with no error, while the control still holds
 * the string — so the cross-field date validators compare garbage too.
 *
 * The state layer holds BOTH shapes, and which one a code path sees is not a
 * detail it can choose:
 *   - the MODIFIED slice carries `Date` objects (written from the form's own
 *     `getRawValue()`), and
 *   - the ORIGINAL slice carries the API's raw strings — `"dd/MM/yyyy"` today.
 * Any path that writes a state slice into a form must therefore convert, and
 * that includes the cross-tab adoption path: a sibling tab's DISCARD
 * republishes the ORIGINAL slice as the new modified state, so the strings
 * reach a mounted form there just as they do at mount.
 *
 * DI-free so it is unit-testable without TestBed. Extracted from the three
 * byte-for-byte-similar private `convertDateStringsToDateObjects` methods that
 * role0 / role1 / role2 each carried; role3 previously hand-rolled a narrower
 * bare `new Date(...)` per qname, which mis-parses the `"dd/MM/yyyy"` form.
 */

/**
 * The minimum a schema control must expose to be classified. Deliberately
 * STRUCTURAL rather than the `FormControlSchema` model type: the sheets do not
 * all share one declaration of that interface (role1 and role3 each narrow
 * `ui.inputFilter` / `validators` differently), so importing any single one of
 * them would make this util unusable from the others.
 */
export interface DateControlLike {
  qname: string;
  ui?: { control?: string };
}

/** API date shape, e.g. `"01/01/2023"` or `"1/1/2023"`. Strict: 4-digit year. */
const DD_MM_YYYY = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

/**
 * The qnames of every `ui.control === 'date'` field in a sheet's schema.
 * Tolerates a missing/half-loaded schema so a caller can stay a one-liner.
 */
export function dateQnamesFromSchema(
  controls: readonly DateControlLike[] | undefined | null
): string[] {
  return (controls ?? [])
    .filter((control) => control?.ui?.control === 'date')
    .map((control) => control.qname);
}

/**
 * Parse ONE date string the way the API means it: `"dd/MM/yyyy"` day-first,
 * anything else through `Date`'s native parser (ISO 8601 above all). Returns
 * `null` for an empty or unparseable value, never an Invalid Date.
 *
 * Use this instead of a bare `new Date(str)` for any API date: V8 reads
 * `"05/03/2024"` month-first (3 May) and `"31/12/2023"` as Invalid Date.
 */
export function parseApiDateString(value: string | null | undefined): Date | null {
  if (!value) return null;

  const ddMmYyyy = value.match(DD_MM_YYYY);
  const parsed = ddMmYyyy
    ? new Date(Number(ddMmYyyy[3]), Number(ddMmYyyy[2]) - 1, Number(ddMmYyyy[1]))
    // Fallback: whatever `Date` recognises natively — ISO 8601 above all.
    : new Date(value);

  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Return a COPY of `data` with every named field's string value replaced by a
 * `Date`. A value that is already a `Date`, is empty, or parses to an invalid
 * date is left exactly as it was — this never invents or blanks a value.
 */
export function coerceDateStringsToDates(
  data: Record<string, any>,
  dateQnames: readonly string[]
): Record<string, any> {
  const converted = { ...data };

  dateQnames.forEach((qname) => {
    const value = converted[qname];
    if (!value || typeof value !== 'string') return;

    const parsed = parseApiDateString(value);
    if (parsed) {
      converted[qname] = parsed;
    }
  });

  return converted;
}

/**
 * Convenience for the common call: coerce every date field a schema declares.
 * The two halves stay exported separately for callers that already know their
 * date qnames (or hold no schema).
 */
export function coerceSchemaDateStrings(
  data: Record<string, any>,
  controls: readonly DateControlLike[] | undefined | null
): Record<string, any> {
  return coerceDateStringsToDates(data, dateQnamesFromSchema(controls));
}
